import React, { useEffect, useState } from 'react';
import { openChannel, MSG, ROLE } from './displayChannel';
import { slideText, nextSlideText } from '../lib/slides';

const formatClock = (date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * Stage / confidence screen (/stage) for the preacher and band: what is on the
 * projector now, what comes next, and the time. Uses the same channel as the
 * projector; text is rendered as React text, never innerHTML.
 */
const StageScreen = () => {
  const [state, setState] = useState({ isLive: false, content: { type: 'welcome' }, currentLine: 0, stage: null });
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    document.title = 'SleekHear Stage';
    const channel = openChannel();
    if (!channel) return undefined;
    channel.onmessage = (e) => {
      if (e.data && e.data.type === MSG.STATE && e.data.state) setState(e.data.state);
    };
    channel.postMessage({ type: MSG.HELLO, role: ROLE.STAGE });
    const onLeave = () => channel.postMessage({ type: MSG.BYE, role: ROLE.STAGE });
    window.addEventListener('pagehide', onLeave);
    return () => {
      window.removeEventListener('pagehide', onLeave);
      channel.close();
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const { isLive, content, currentLine, slides, stage } = state;
  const label = { fontSize: '1.6vw', letterSpacing: '0.2em', opacity: 0.6, textTransform: 'uppercase', marginBottom: '1vw' };

  let current = null;
  let upcoming = null;
  if (content && content.type === 'scripture') {
    current = (
      <>
        <div style={{ fontSize: '3.2vw', fontWeight: 'bold', marginBottom: '1.5vw' }}>
          {content.reference}{content.version ? ` (${content.version})` : ''}
        </div>
        <div style={{ fontSize: '3.6vw', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{slideText(content, slides, currentLine)}</div>
      </>
    );
    if (Array.isArray(slides) && slides.length > 1) {
      const next = nextSlideText(content, slides, currentLine);
      upcoming = (
        <div style={{ marginTop: '2vw' }}>
          <div style={label}>Slide {Math.min(currentLine, slides.length - 1) + 1} of {slides.length}</div>
          <div style={{ fontSize: '2.6vw', color: '#fbbf24', whiteSpace: 'pre-wrap' }}>{next || '— end of passage —'}</div>
        </div>
      );
    }
  } else if (content && content.type === 'song') {
    current = (
      <>
        <div style={{ fontSize: '2.2vw', opacity: 0.7, marginBottom: '1.5vw' }}>{content.title}</div>
        <div style={{ fontSize: '6vw', fontWeight: 'bold', lineHeight: 1.3, whiteSpace: 'pre-wrap' }}>{slideText(content, slides, currentLine)}</div>
      </>
    );
    const nextLine = nextSlideText(content, slides, currentLine);
    upcoming = (
      <div style={{ marginTop: '2vw' }}>
        <div style={label}>Next line</div>
        <div style={{ fontSize: '3.4vw', color: '#fbbf24', whiteSpace: 'pre-wrap' }}>{nextLine || '— end of song —'}</div>
      </div>
    );
  }

  return (
    <div
      data-testid="stage-root"
      style={{
        position: 'fixed', inset: 0, background: '#000', color: '#fff', fontFamily: 'Arial, sans-serif',
        display: 'flex', flexDirection: 'column', padding: '2vw 3vw', boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div data-testid="stage-clock" style={{ fontSize: '6vw', fontWeight: 'bold' }}>{formatClock(now)}</div>
        <div style={{ textAlign: 'right' }}>
          <div
            data-testid="stage-status"
            style={{
              display: 'inline-block', fontSize: '2vw', fontWeight: 'bold', padding: '0.4vw 1.6vw', borderRadius: '1vw',
              background: isLive ? '#dc2626' : '#374151',
            }}
          >
            {isLive ? 'LIVE' : 'OFF AIR'}
          </div>
          {stage && stage.total > 0 && (
            <div style={{ fontSize: '1.6vw', opacity: 0.6, marginTop: '0.6vw' }}>
              {stage.planName ? `${stage.planName} · ` : ''}
              {stage.index >= 0 ? `${stage.index + 1} / ${stage.total}` : `${stage.total} items`}
            </div>
          )}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', opacity: isLive ? 1 : 0.45 }}>
        <div style={label}>On screen</div>
        {current || <div style={{ fontSize: '3vw', opacity: 0.5 }}>Nothing on screen</div>}
        {upcoming}
      </div>

      <div style={{ borderTop: '2px solid #374151', paddingTop: '1.2vw' }}>
        <div style={label}>Next up</div>
        <div data-testid="stage-next" style={{ fontSize: '3.4vw', color: '#fbbf24' }}>
          {stage && stage.nextLabel ? stage.nextLabel : '—'}
        </div>
      </div>
    </div>
  );
};

export default StageScreen;
